/* File: PrivacyPolicy.jsx */
import React, { useEffect, useRef } from "react";
import "../../assets/css/privacy-policy.scss";

export default function PrivacyPolicy() {
  const sectionsRef = useRef([]);

  useEffect(() => {
    // Scrollspy & reveal on scroll
    const sections = (sectionsRef.current = Array.from(
      document.querySelectorAll(".pp-section")
    ));
    const tocLinks = Array.from(document.querySelectorAll(".pp-toc a"));

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("id");
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            tocLinks.forEach((a) =>
              a.classList.toggle("active", a.getAttribute("href") === `#${id}`)
            );
          }
        });
      },
      { root: null, rootMargin: "0px 0px -30% 0px", threshold: 0.1 }
    );

    sections.forEach((s) => io.observe(s));

    // Smooth internal link scrolling
    const anchors = document.querySelectorAll('a[href^="#"]');
    const smoothScroll = (e) => {
      e.preventDefault();
      const target = document.querySelector(
        e.currentTarget.getAttribute("href")
      );
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    anchors.forEach((a) => a.addEventListener("click", smoothScroll));

    return () => {
      io.disconnect();
      anchors.forEach((a) => a.removeEventListener("click", smoothScroll));
    };
  }, []);

  return (
    <main className="pp-page" aria-labelledby="pp-title">
      <div className="pp-hero">
        <div className="pp-hero-inner">
          <h1 id="pp-title">DREAMSTARS PRIVACY NOTICE</h1>
          <p className="pp-sub">
            DREAMSTARS FRAMEWORK TERMS — Last updated: Oct 4, 2022
          </p>
        </div>
        <div aria-hidden className="pp-hero-ornaments">
          <span className="orb orb-1" />
          <span className="orb orb-2" />
          <span className="orb orb-3" />
        </div>
      </div>

      <div className="pp-container">
        <aside className="pp-toc" aria-label="Table of contents">
          <strong>On this page</strong>
          <ul>
            <li>
              <a href="#about-us">1. Information About Us</a>
            </li>
            <li>
              <a href="#contract-info">
                2. Contract Information & Correspondence
              </a>
            </li>
            <li>
              <a href="#marketing">3. Marketing</a>
            </li>
            <li>
              <a href="#website-info">4. Website Information</a>
            </li>
            <li>
              <a href="#cookies">5. Cookies</a>
            </li>
            <li>
              <a href="#content">6. Content</a>
            </li>
            <li>
              <a href="#employee">7. Employee Information</a>
            </li>
            <li>
              <a href="#applications">8. Job Applications</a>
            </li>
            <li>
              <a href="#legal">9. Legal Claims</a>
            </li>
            <li>
              <a href="#third-parties">10. Info from Third Parties</a>
            </li>
            <li>
              <a href="#special">11. Special Categories</a>
            </li>
            <li>
              <a href="#uses">12. Why Else We Use Info</a>
            </li>
            <li>
              <a href="#sharing">13. Sharing Your Information</a>
            </li>
            <li>
              <a href="#where">14. Where We Store</a>
            </li>
            <li>
              <a href="#security">15. Data Security</a>
            </li>
            <li>
              <a href="#retention">16. Retention</a>
            </li>
            <li>
              <a href="#rights">17. Your Rights</a>
            </li>
            <li>
              <a href="#changes">18. Changes</a>
            </li>
          </ul>
        </aside>

        <article className="pp-article">
          <section id="about-us" className="pp-section">
            <h2>1. INFORMATION ABOUT US</h2>
            <p>
              DreamStars Ltd is committed to protecting the privacy and security
              of your personal information. This privacy notice describes how we
              collect and use personal information about you in accordance with
              data protection law. Please read it carefully.
            </p>
            <div className="card">
              <h3>Contact & Registered Office</h3>
              {/* <p>Floor 2, 51-53 Great Marlborough Street, London, England, W1F 7JT</p> */}
              <p>Company number: 09589798</p>
              <p>Phone: +44 (0) 20 7100 4158</p>
              <p>
                Email:{" "}
                <a href="mailto:contact@DreamStars.com">
                  contact@DreamStars.com
                </a>
              </p>
            </div>
          </section>

          <section id="contract-info" className="pp-section">
            <h2>2. CONTRACT INFORMATION AND OTHER CORRESPONDENCE</h2>
            <p>
              When you enter into a contract with us (or someone does so on your
              behalf) there will be personal information about you relating to
              that contract such as your name, contact details, contract
              details, delivery details, and correspondence with us about the
              contract.
            </p>

            <ol>
              <li>
                We need certain information to carry out our contract with you —
                mandatory fields will be marked with an asterisk.
              </li>
              <li>
                We may collect correspondence, call information and any
                queries/complaints you raise about services.
              </li>
              <li>
                We retain contract-related information up to 6 years after
                account closure (where required for tax or claims).
              </li>
              <li>
                Payment information is retained by our payment processor for 3
                years after deactivation.
              </li>
            </ol>
          </section>

          <section id="marketing" className="pp-section">
            <h2>3. MARKETING</h2>
            <p>
              We may collect your name and contact details to send information
              about products and services you might be interested in. You always
              have the right to opt out of marketing — unsubscribe links are
              included in emails.
            </p>
            <p className="muted">
              We keep suppression lists indefinitely to avoid accidental
              marketing after an opt-out.
            </p>
          </section>

          <section id="website-info" className="pp-section">
            <h2>4. WEBSITE INFORMATION</h2>
            <p>
              We may collect information about your use of our website via
              cookies, analytics and other technical means to ensure the site
              operates effectively and securely.
            </p>
            <p>
              Website information is kept for 12 months from collection, or
              until the cookie expires.
            </p>
          </section>

          <section id="cookies" className="pp-section">
            <h2>5. COOKIES</h2>
            <p>
              We use cookies to distinguish you from other users and to provide
              a better experience. Cookies include:
            </p>
            <ul>
              <li>
                <strong>Strictly necessary cookies</strong> — essential for
                platform operation.
              </li>
              <li>
                <strong>Analytical/performance cookies</strong> — help us
                understand how users move through the site.
              </li>
              <li>
                <strong>Functionality cookies</strong> — remember preferences
                and personalise content.
              </li>
              <li>
                <strong>Targeting cookies</strong> — record browsing to help
                tailor adverts; may be used by third parties.
              </li>
            </ul>
            <p>Blocking cookies may limit access to parts of the platform.</p>
          </section>

          <section id="content" className="pp-section">
            <h2>6. CONTENT</h2>
            <p>
              Content you post (reviews, photos, videos) may be displayed on our
              platform and may be retained or displayed indefinitely even after
              account closure, depending on the nature of the content.
            </p>
          </section>

          <section id="employee" className="pp-section">
            <h2>7. EMPLOYEE INFORMATION</h2>
            <p>
              If you work for one of our customers, suppliers or partners we may
              hold contact and employment details to manage our relationship.
              This is kept for up to seven years after the relationship ends.
            </p>
          </section>

          <section id="applications" className="pp-section">
            <h2>8. JOB APPLICATIONS</h2>
            <p>
              We process applicant information necessary for recruitment and to
              fulfil legal obligations. If unsuccessful, application data is
              retained for up to six months after recruitment round end.
            </p>
          </section>

          <section id="legal" className="pp-section">
            <h2>9. LEGAL CLAIMS</h2>
            <p>
              Where legal claims are possible, we may retain personal
              information as necessary to bring or defend claims and share
              information with legal advisers or insurers as required.
            </p>
          </section>

          <section id="third-parties" className="pp-section">
            <h2>10. INFORMATION WE RECEIVE FROM THIRD PARTIES</h2>
            <p>
              We may receive information from service providers, acquired
              businesses, our other channels, publicly available sources (e.g.,
              Companies House, LinkedIn, social platforms) and credit reference
              agencies.
            </p>
          </section>

          <section id="special" className="pp-section">
            <h2>11. SPECIAL CATEGORIES OF DATA</h2>
            <p>
              We may process sensitive data (race, religion, sexual orientation,
              political opinions) only in specific circumstances and usually
              with explicit consent or where legally permitted.
            </p>
          </section>

          <section id="uses" className="pp-section">
            <h2>12. WHY ELSE DO WE USE YOUR INFORMATION?</h2>
            <ol>
              <li>To perform contracts with you.</li>
              <li>To comply with legal obligations.</li>
              <li>For legitimate interests where appropriate.</li>
            </ol>
          </section>

          <section id="sharing" className="pp-section">
            <h2>13. SHARING YOUR INFORMATION</h2>
            <p>
              We share information with third parties (service providers, group
              entities) as required to deliver services. We do not sell your
              data. Third parties are contractually required to keep data
              secure.
            </p>
            <p className="muted">
              Examples: Google Analytics for site analytics and other listed
              processors.
            </p>
          </section>

          <section id="where" className="pp-section">
            <h2>14. WHERE WE STORE YOUR INFORMATION</h2>
            <p>
              Our headquarters and main data centre are in the UK. Data may be
              transferred and processed outside the UK/EU where necessary;
              appropriate safeguards (e.g. model clauses) are used where
              required.
            </p>
          </section>

          <section id="security" className="pp-section">
            <h2>15. DATA SECURITY</h2>
            <p>
              We have internal security measures to prevent accidental loss,
              unauthorised access, or disclosure and will notify affected
              parties and regulators if a breach requires it.
            </p>
          </section>

          <section id="retention" className="pp-section">
            <h2>16. HOW LONG WILL WE KEEP YOUR INFORMATION FOR?</h2>
            <p>
              Retention periods depend on the data type and legal requirements.
              We may anonymise data for continued use.
            </p>
          </section>

          <section id="rights" className="pp-section">
            <h2>17. YOUR RIGHTS</h2>
            <p>
              You have rights to access, correct, erase, restrict, object,
              withdraw consent, and portability where applicable. Contact
              details for exercising rights are provided in section 1.
            </p>
            <p className="muted">
              We will normally respond within one month, unless an extension is
              required.
            </p>
          </section>

          <section id="changes" className="pp-section">
            <h2>18. CHANGES TO THIS PRIVACY NOTICE</h2>
            <p>
              Changes will be posted on this page and, where appropriate,
              notified by e-mail. Please check back frequently.
            </p>
          </section>

          <footer className="pp-footer">
            <p>© {new Date().getFullYear()} DreamStars Ltd </p>
          </footer>
        </article>
      </div>
    </main>
  );
}

/* File: privacy-policy.scss */
